import {Injectable} from '@angular/core';
import {RestProvider, RestRoutes} from "../rest/rest";
import {
  Content,
  ContentId,
  DifficultyRoute,
  DifficultyVia,
  EntityType,
  KeyStorage,
  LastUpdate,
  Location,
  Photo,
  Route,
  SearchFilter,
  Sector,
  SectorResult,
  SectorType,
  Site,
  SiteResult,
  StorageImage
} from "../../models/data.model";
import {ToastProvider} from "../toast/toast";
import {Loading, LoadingController} from "ionic-angular";
import {Storage} from "@ionic/storage";
import {Geolocation, GeolocationOptions, Geoposition} from "@ionic-native/geolocation";

@Injectable()
export class DataProvider {

  sites: Site[];
  sectorsOffline: number[] = [];
  searchFilter: SearchFilter;
  searchResult: SiteResult[];
  searchResultCounts = {
    sites: 0,
    sectors: 0,
    routes: 0
  };
  difficultiesVia: string[];
  difficultiesRoute: string[];
  contactContent: Content = null;
  contentBook: Content = null;
  environmentalAlert: Content = null;
  geoOptions : GeolocationOptions = {
    enableHighAccuracy : true,
    maximumAge: 5000
  };
  currentPosition: Location = null;

  constructor(public rest: RestProvider,
              public toast: ToastProvider,
              public loadingCtrl: LoadingController,
              private storage: Storage,
              public geolocation: Geolocation) {
    let loader: Loading = this.loadingCtrl.create({
      spinner: 'crescent',
      content: 'Chargement...'
    });
    loader.present().catch();
    this.getAllDataStorage().then(() => {
      this.getLastUpdate()
        .then(lastUpdate => {
          this.downloadSites(lastUpdate.sites)
            .then(() => {
              this.downloadContent(lastUpdate.book, ContentId.Book)
                .then(() => {
                  this.downloadContent(lastUpdate.environmentalAlert, ContentId.EnvironmentalAlert)
                    .then(() => {
                      this.downloadContent(lastUpdate.contact, ContentId.Contact)
                        .then(() =>{
                          loader.dismiss().catch();
                        }).catch(() => {
                        loader.dismiss().catch();
                        this.toast.errorToast("Erreur lors de la consultation des Contacts.");
                      })
                    }).catch(() => {
                    loader.dismiss().catch();
                    this.toast.errorToast("Erreur lors de la consultation des Alertes env.");
                  })
                }).catch(() => {
                loader.dismiss().catch();
                this.toast.errorToast("Erreur lors de la consultation des infos Topoguide");
              })
            }).catch(() => {
              loader.dismiss().catch();
              this.toast.errorToast("Erreur lors de la consultation des sites");
            })
        }).catch(() => {
          this.toast.errorToast("Erreur lors du rafraichissement des données");
          loader.dismiss().catch();
        });
    }).catch(() => {
      this.toast.errorToast("Erreur lors de la lecture des données");
      loader.dismiss().catch();
    });
    this.initSearchFilter();
    this.initDifficulties();
  }


  private getAllDataStorage(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getSectorsOfflineStorage().then(sectorsOffline => {
        this.sectorsOffline = [...sectorsOffline];
        this.getSitesStorage()
          .then(sites => {
            this.sites = [...sites];
            this.getContentStorage(ContentId.Book)
              .then(contentBook => {
                this.contentBook = contentBook;
                this.getContentStorage(ContentId.EnvironmentalAlert)
                  .then(contentEnvironmentalAlert => {
                    this.environmentalAlert = contentEnvironmentalAlert;
                    this.getContentStorage(ContentId.Contact)
                      .then( contactContent => {
                        this.contactContent = contactContent
                        resolve();
                      }).catch(() => reject("Contact"));;
                  }).catch(() => reject("Alerte environnementale"));
              }).catch(() => reject("Topoguide"));
          }).catch(() => reject("Sites"));
      }).catch(() => reject("Secteurs hors ligne"));
    });
  }

  private isSitesUpdate(lastUpdateServer: Date): boolean {
    if (this.getSites().length == 0 ) return false;
    let lastUpdateSite: Date = null;
    this.getSites().forEach(site => {
      if (lastUpdateSite == null) lastUpdateSite = site.lastUpdate;
      else if (lastUpdateSite < site.lastUpdate) lastUpdateSite = site.lastUpdate;
    });
    return lastUpdateSite == lastUpdateServer;
  }

  private isContentUpdate(lastUpdateServer: Date, contentId: ContentId): boolean {
    //let content: Content = ((contentId == ContentId.Book) ? this.contentBook : this.environmentalAlert);
    let content: Content;;
    switch (contentId) {
      case ContentId.Book:
        content = this.contentBook;
        break;
      case ContentId.EnvironmentalAlert:
        content = this.environmentalAlert;
        break;
      case ContentId.Contact:
        content = this.contactContent;
        break;
    }
    if (content == null) return false;
    return content.lastUpdate == lastUpdateServer;
  }

  private downloadSites(lastUpdate: Date): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.isSitesUpdate(lastUpdate)) resolve();
      else {
        this.getSitesRest()
          .then(sites => {
            this.sites = [...sites];
            this.storeSites(sites).catch(reject);
          }).then(resolve).catch(reject)
          .catch(reject);
      }
    });
  }

  private downloadContent(lastUpdate: Date, contentId: ContentId): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.isContentUpdate(lastUpdate, contentId)) resolve();
      else {
        this.getContentRest(contentId).then(content => {
          if (contentId == ContentId.Book) this.contentBook = content;
          else if (contentId == ContentId.EnvironmentalAlert) this.environmentalAlert = content;
          else if (contentId == ContentId.Contact) this.contactContent = content;
          this.storeContent(content).then(resolve).catch(reject);
        }).catch(reject);
      }
    });
  }

  public isSectorOffline(sectorId: number): boolean {
    return this.sectorsOffline.findIndex(sectorOffline => {
      return sectorOffline == sectorId;
    }) !== -1;
  }

  public initSearchFilter(): void {
    this.searchFilter = {
      name: "",
      isDifficultyRoute: false,
      difficultyRoute: {
        lower: 0,
        upper: Object.keys(DifficultyRoute).map(c => DifficultyRoute[c]).length-1
      },
      isDifficultyVia: false,
      difficultyVia: {
        lower: 0,
        upper: Object.keys(DifficultyVia).map(c => DifficultyVia[c]).length-1
      },
      isDistance: false,
      distance: 50,
      isSectorType: false,
      sectorType : {}
    };
    Object.keys(SectorType).forEach((sectorType: SectorType) => {
      this.searchFilter.sectorType[sectorType] = false;
    });
  }

  public getSectorsOfflineStorage(): Promise<number[]> {
    return new Promise((resolve, reject) => {
      this.storage.get(KeyStorage.SectorsOffline).then((sectors: number[]) => {
        if (sectors == null) sectors = [];
        resolve(sectors);
      }).catch(reject);
    });
  }

  public getSitesStorage(): Promise<Site[]> {
    return new Promise((resolve, reject) => {
      this.storage.get(KeyStorage.Sites).then((sites: Site[]) => {
        if (sites == null) sites = [];
        resolve(sites);
      }).catch(reject);
    });
  }

  public getSitesRest(): Promise<Site[]> {
    return new Promise((resolve, reject) => {
      this.rest.getAll<Site>(RestRoutes.sites, false)
        .then((sites: Site[]) => { resolve(sites); })
        .catch(() => {
          this.toast.errorToast("Erreur lors de la récupération des sites");
          reject();
        });
    });
  }

  private getLastUpdate(): Promise<LastUpdate> {
    return new Promise((resolve, reject) => {
      this.rest.get<LastUpdate>("", RestRoutes.lastUpdate, false)
        .then(lastUpdate => { resolve(lastUpdate); })
        .catch((err) => {
          this.toast.errorToast("Erreur lors des mises à jour");
          reject(err);
        });
    });
  }

  public getContentStorage(contentId: ContentId): Promise<Content> {
    return new Promise((resolve, reject) => {
      this.storage.get(KeyStorage.Content).then((contentList: Content[]) => {
        let index: number = -1;
        if (contentList == null) contentList = [];
        if (contentList.length > 0) index = contentList.findIndex(content_ => {
          return content_.id === contentId;
        });
        if (index === -1) resolve(null);
        else resolve(contentList[index]);
      }).catch(reject);
    });
  }

  public getContentRest(contentId: string): Promise<Content> {
    return new Promise((resolve, reject) => {
      this.rest.get<Content>(contentId, RestRoutes.content, false)
        .then((content) => { resolve(content); })
        .catch(() => {
          this.toast.errorToast("Erreur lors de la récupération du texte");
          reject();
        })
    });
  }

  private storeSites(sites: Site[]): Promise<Site[]> {
    return new Promise((resolve, reject) => {
      this.storage.set(KeyStorage.Sites, sites).then(() => {
        resolve(sites);
      }).catch(() => {
        this.toast.errorToast("Erreur lors de la sauvegarde des sites en mémoire");
        reject();
      });
    });
  }

  private storeContent(content: Content): Promise<Content> {
    return new Promise((resolve, reject) => {
      this.storage.get(KeyStorage.Content)
        .then((contentList: Content[]) => {
          let indexToInsert: number = -1;
          if (contentList == null) contentList = [];
          if (contentList.length > 0) indexToInsert = contentList.findIndex(content_ => {
            return content_.id === content.id;
          });
          if (indexToInsert === -1) contentList.push(content);
          else contentList[indexToInsert] = content;
          this.storage.set(KeyStorage.Content, contentList)
            .then(() => {
              resolve(content);
            })
            .catch(() => {
              this.toast.errorToast("Erreur lors de la sauvegarde en mémoire");
              reject();
            });
        })
        .catch(() => {
          this.toast.errorToast("Erreur lors de la sauvegarde en mémoire");
          reject();
        });
    });
  }

  public getSites(): Site[] { return this.sites; }

  public getSite(siteId: number): Site { return this.getSites().find((site) => {return site.id === siteId}); }

  public getSector(siteId: number, sectorId: number): Sector {
    return this.getSite(siteId).sectors.find((sector) => {return sector.id === sectorId});
  }

  public getRoute(siteId: number, sectorId: number, routeId: number) {
    return this.getSector(siteId, sectorId).routes.find((route) => {return route.id === routeId});
  }

  static getPhotoUrl(photo: string, isThumb: boolean): string {
    return "https://appbase.escalaguil.fr/photos" + (isThumb ? "_thumb/" : "/") + photo;
  }

  public getPhotosOffline(photosName: string[]): Promise<Photo[]> {
    return new Promise<Photo[]>((resolve, reject) => {
      let photos: Photo[] = [];
      let getPromises: Promise<any>[] = [];
      photosName.forEach(photoName => {
        getPromises.push(new Promise(resolve => {
          this.storage.get(KeyStorage.Photos + photoName).then((photoStorage: StorageImage) => {
            photos.push({
              name: photoName,
              url: photoStorage.dataBase64
            });
            resolve();
          })
        }));
      });
      Promise.all(getPromises)
        .then(() => { resolve(photos); })
        .catch(err => { reject(err); })
    });
  }

  static getSiteNumberRoutes(site: Site): number {
    let routes = 0;
    for (let sector of site.sectors) {
      routes += sector.routes.length;
    }
    return routes;
  }

  public getSearchFilter(): SearchFilter {
    return this.searchFilter;
  }

  public setSearchFilter(searchFilter_: SearchFilter): void {
    this.searchFilter = JSON.parse(JSON.stringify(searchFilter_));
    this.updateSearchResult();
    this.searchResultCounts = {
      sites: 0,
      sectors: 0,
      routes: 0
    };
    this.searchResult.forEach(site => {
      site.sectorResults.forEach(sector => {
        this.searchResultCounts.routes += sector.routeResults.length;
        this.searchResultCounts.sectors++;
      });
      this.searchResultCounts.sites++;
    });
  }

  public getCountSearchResult(entityType: EntityType): number {
    if (entityType == EntityType.Site) return this.searchResultCounts.sites;
    if (entityType == EntityType.Sector) return this.searchResultCounts.sectors;
    return this.searchResultCounts.routes;
  }

  public getSearchResult() {
    return this.searchResult;
  }

  private updateSearchResult() {
    let searchResult: SiteResult[] = [];
    this.getSites().forEach(site => {
      if (this.isSatisfyingSite(site)) {
        let sectorResults: SectorResult[] = [];
        site.sectors.forEach(sector => {
          if (this.isSatisfyingSector(sector)) {
            let routeResults: Route[] = [];
            sector.routes.forEach(route => {
              if (this.isSatisfyingRoute(sector, route)) {
                routeResults.push(route);
              }
            });
            if ((routeResults.length > 0) || (!this.isDifficulty() && this.isSatisfyingName(sector))) {
              sectorResults.push({
                sector: sector,
                routeResults: routeResults
              });
            }
          }
        });
        if (sectorResults.length > 0 || (!this.isDifficulty() && this.isSatisfyingName(site))) searchResult.push({
          site: site,
          sectorResults: sectorResults
        });
      }
    });
    this.searchResult = searchResult;
  }

  private isDifficulty(): boolean {
    return this.getSearchFilter().isDifficultyRoute || this.getSearchFilter().isDifficultyVia;
  }

  private isSatisfyingName(entity: Site | Sector | Route): boolean {
    let searchFilter: SearchFilter = this.getSearchFilter();
    return !(searchFilter.name.length > 0 && !entity.name.toLowerCase().includes(searchFilter.name.toLowerCase()));

  }

  private isSatisfyingRoute(sector: Sector, route: Route): boolean {
    let searchFilter: SearchFilter = this.getSearchFilter();
    if (!this.isSatisfyingName(route) && searchFilter.difficultyRoute) return false;
    if (sector.type !== SectorType.ViaFerrata && searchFilter.difficultyRoute &&
      !this.isSatisfyingRouteDifficulty(route)) return false;
    return !(sector.type == SectorType.ViaFerrata && searchFilter.isDifficultyVia &&
      !this.isSatisfyingViaDifficulty(route));
  }

  private isSatisfyingRouteDifficulty(route: Route): boolean {
    let difficultyRouteIndex = this.difficultiesRoute.indexOf(route.difficulty);
    return (this.getSearchFilter().difficultyRoute.lower <= difficultyRouteIndex &&
      difficultyRouteIndex <= this.getSearchFilter().difficultyRoute.upper);
  }

  private isSatisfyingViaDifficulty(route: Route): boolean {
    let difficultyRouteIndex = this.difficultiesVia.indexOf(route.difficulty);
    return (this.getSearchFilter().difficultyVia.lower <= difficultyRouteIndex &&
      difficultyRouteIndex <= this.getSearchFilter().difficultyVia.upper);
  }

  private isSatisfyingSector(sector: Sector): boolean { // not filtering name
    let searchFilter: SearchFilter = this.getSearchFilter();
    if (searchFilter.isDistance && DataProvider.distance(this.currentPosition, sector.location) > searchFilter.distance) return false;
    if (searchFilter.isSectorType) {
      let sectorTypeEnum = Object.keys(SectorType).find((sectorType: SectorType) => {
        return (SectorType[sectorType] == sector.type);
      });
      if (! searchFilter.sectorType[sectorTypeEnum]) return false;
    }
    return true;
  }

  private isSatisfyingSite(site: Site): boolean {
    let searchFilter: SearchFilter = this.getSearchFilter();
    if (this.currentPosition == null) return true;
    return !(searchFilter.isDistance && DataProvider.distance(this.currentPosition, site.location) > searchFilter.distance);
  }

  private static distance(loc1: Location, loc2: Location) : number  {
    const dLat = DataProvider.toRad(loc1.latitude - loc2.latitude);
    const dLon = DataProvider.toRad(loc2.longitude - loc2.longitude);
    const lat1 = DataProvider.toRad(loc1.latitude);
    const lat2 = DataProvider.toRad(loc2.latitude);

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
    return 6371 * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
  }

  private static toRad(value) {
    return value * Math.PI / 180;
  }

  private initDifficulties() {
    this.difficultiesVia = Object.keys(DifficultyVia).map(c => DifficultyVia[c]);
    this.difficultiesRoute = Object.keys(DifficultyRoute).map(c => DifficultyRoute[c]);
  }

  public getBookContent(): Content {
    return this.contentBook;
  }

  public getContactContent(): Content {
    return this.contactContent;
  }

  public getEnvironmentalAlertContent(): Content {
    return this.environmentalAlert;
  }

  public downloadSector(siteId: number, sectorId: number) {
    let loader: Loading = this.loadingCtrl.create({
      spinner: 'crescent',
      content: 'Téléchargement du secteur...'
    });
    loader.present().catch();
    this.downloadPhotoSector(sectorId, this.getSector(siteId, sectorId).photos, 0)
      .then(() => {
        this.toast.successToast(this.getSector(siteId, sectorId).name + " téléchargé");
        let photosPoi: string[] = [];
        this.getSector(siteId, sectorId).photoPaths.forEach(photoPath => {
          photoPath.pois.forEach(poi => {
            if (poi.photo) photosPoi.push(poi.photo);
          });
        });
        if (photosPoi.length > 0) {
          this.downloadPhotoSector(sectorId, photosPoi, 0)
            .then(() => {
              loader.dismiss().catch();
            }).catch(() => {
              this.toast.errorToast("Erreur lors du téléchargement du secteur");
              loader.dismiss().catch();
            })
        } else loader.dismiss().catch();
      })
      .catch(() => {
      this.toast.errorToast("Erreur lors du téléchargement du secteur");
      loader.dismiss().catch();
    });
  }

  private downloadPhotoSector(sectorId: number, photos: string[], index: number) : Promise<any> {
    return new Promise<any>((resolve, reject) => {
      RestProvider.getPhoto(photos[index]).then(photo64 => {
        this.storage.get(KeyStorage.Photos + photos[index])
          .then((photoStorage: StorageImage) => {
            if (photoStorage == null) photoStorage = { dataBase64: photo64, sectors: []};
            photoStorage.sectors.push(sectorId);
            this.storage.set(KeyStorage.Photos + photos[index], photoStorage)
              .then(() => {
                if (this.sectorsOffline.indexOf(sectorId) == -1) this.sectorsOffline.push(sectorId);
                this.storage.set(KeyStorage.SectorsOffline, this.sectorsOffline)
                  .then(() => {
                    if (index < photos.length) {
                      this.downloadPhotoSector(sectorId, photos, index+1)
                        .then(resolve).catch(reject);
                    } else resolve();
                  }).catch(reject);
              }).catch(reject);
          }).catch(reject);
      }).catch(reject);
    });
  }

  public deleteSectorOffline(siteId: number, sectorId: number) {
    let loader: Loading = this.loadingCtrl.create({
      spinner: 'crescent',
      content: 'Téléchargement du secteur...'
    });
    loader.present().catch();
    let promises: Promise<any>[] = [];
    this.getSector(siteId, sectorId).photos.forEach(photo => {
      promises.push(new Promise<any>((resolve, reject) => {
        this.storage.get(KeyStorage.Photos + photo)
          .then((photoStorage: StorageImage) => {
            if (photoStorage == null) resolve();
            if (photoStorage.sectors.length === 1) {
              if (photoStorage.sectors[0] != sectorId) resolve();
              this.storage.remove(KeyStorage.Photos + photo)
                .then(() => { resolve(); })
                .catch(() => { reject(); });
            } else {
              let indexSector = photoStorage.sectors.indexOf(sectorId);
              if (indexSector >= 0) photoStorage.sectors.splice(indexSector, 1);
              this.storage.set(KeyStorage.Photos + photo, photoStorage)
                .then(() => { resolve(); })
                .catch(() => { reject(); });
            }
          })
          .catch(() => { reject(); });
      }));
    });
    promises.reduce((promiseChain, currentTask) => {
      return promiseChain.then(chainResults =>
        currentTask.then(currentResult => [ ...chainResults, currentResult ])
      );
    }, Promise.resolve([])).then(() => {
      let indexSector = this.sectorsOffline.indexOf(sectorId);
      if (indexSector >= 0) this.sectorsOffline.splice(indexSector, 1);
      this.storage.set(KeyStorage.SectorsOffline, this.sectorsOffline).catch();
      loader.dismiss().catch();
    }).catch(() => {
      this.toast.errorToast("Erreur lors de la suppression du secteur");
      loader.dismiss().catch();
    });
  }

  public clearAllData() {
    this.storage.forEach((value, key) => {
      if (key != KeyStorage.Content && key != KeyStorage.Sites) {
        this.storage.remove(key).catch();
      }
    }).catch();
    this.sectorsOffline = [];
  }

  public updateLocation(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let loader: Loading = this.loadingCtrl.create({
        spinner: 'crescent',
        content: 'Localisation...'
      });
      loader.present().catch(reject);
      this.geolocation.getCurrentPosition(this.geoOptions)
        .then((pos : Geoposition) => {
          if (!(pos && pos.coords && pos.coords.latitude && pos.coords.longitude)) {
            this.currentPosition = null;
            loader.dismiss().catch(reject);
            reject();
          }
          else {
            this.currentPosition = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude
            };
            loader.dismiss().catch(reject);
            resolve();
          }
        }).catch(error => {
        this.currentPosition = null;
          loader.dismiss().catch(reject);
          reject();
        });
    });
  }

  public getLocation(): Location {
    return this.currentPosition;
  }

  public isPosition(): boolean {
    return this.currentPosition
      && this.currentPosition.latitude != null
      && this.currentPosition.longitude != null;
  }
}



// WEBPACK FOOTER //
// ./src/providers/data/data.ts